import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Calendar, Clock, CreditCard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { widgetApi } from '../../lib/api';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const widgetId = searchParams.get('widget_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, expired
  const [paymentData, setPaymentData] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const maxPolls = 10;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await widgetApi.getPaymentStatus(sessionId);
        const data = response.data;
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          setPaymentData(data);
          return true;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return true;
        } else {
          // Still pending, continue polling
          return false;
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (pollCount >= maxPolls - 1) {
          setStatus('error');
          return true;
        }
        return false;
      }
    };

    const poll = async () => {
      const isDone = await checkPaymentStatus();
      if (!isDone && pollCount < maxPolls) {
        setPollCount(prev => prev + 1);
        setTimeout(poll, 2000); // Poll every 2 seconds
      }
    };

    poll();
  }, [sessionId, pollCount]);

  const handleBackToWidget = () => {
    if (widgetId) {
      navigate(`/widget/punctual/${widgetId}`);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="text-center space-y-2">
          {status === 'loading' && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
              <CardTitle className="text-xl">Vérification du paiement...</CardTitle>
              <CardDescription>
                Veuillez patienter pendant que nous vérifions votre paiement.
              </CardDescription>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <CardTitle className="text-xl text-green-700">Paiement confirmé !</CardTitle>
              <CardDescription>
                Votre réservation a été confirmée avec succès.
              </CardDescription>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-yellow-600" />
              </div>
              <CardTitle className="text-xl text-yellow-700">Session expirée</CardTitle>
              <CardDescription>
                Votre session de paiement a expiré. Veuillez réessayer.
              </CardDescription>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <CardTitle className="text-xl text-red-700">Erreur</CardTitle>
              <CardDescription>
                Une erreur s'est produite lors de la vérification du paiement.
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {status === 'success' && paymentData && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Référence réservation</p>
                  <p className="font-medium">{paymentData.booking_id?.slice(0, 8)}...</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-500">Statut</p>
                  <p className="font-medium text-green-600">Payé et confirmé</p>
                </div>
              </div>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-700">
                Un email de confirmation a été envoyé à votre adresse email avec tous les détails de votre réservation.
              </p>
            </div>
          )}

          {(status === 'success' || status === 'expired' || status === 'error') && (
            <Button 
              onClick={handleBackToWidget} 
              className="w-full"
              variant={status === 'success' ? 'default' : 'outline'}
            >
              {status === 'success' ? 'Terminé' : 'Retour'}
            </Button>
          )}

          {status === 'loading' && (
            <div className="text-center text-sm text-gray-500">
              <p>Tentative {pollCount + 1} sur {maxPolls}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
